import React from 'react';
import { Brain, Zap, Target } from 'lucide-react';

const MeetVeraSection = () => {
  const features = [
    {
      icon: Brain,
      emoji: '🧠',
      title: 'She Remembers Everything',
      description: 'Vera remembers your goals, your wins, your struggles, and your financial personality. Every conversation builds on the last.',
      example: '"Last March you saved $400 toward your vacation fund. This March you\'re at $120. What changed? I\'m here to help — not judge."',
    },
    {
      icon: Zap,
      emoji: '⚡',
      title: 'She Sees Problems Coming',
      description: 'Vera monitors your spending patterns and alerts you before you go over budget — not after.',
      example: '"You\'ll exceed your grocery budget by $87 before the 15th. Want 3 ways to offset this?"',
    },
    {
      icon: Target,
      emoji: '🎯',
      title: 'She Adapts to You',
      description: 'Vera identifies your money personality and adjusts her entire coaching style — gentle for Avoiders, data-rich for Optimizers, structured for Impulse Spenders.',
      example: null,
    },
  ];

  const personalities = [
    { emoji: '🚫', name: 'The Avoider' },
    { emoji: '📊', name: 'The Optimizer' },
    { emoji: '💸', name: 'The Impulse Spender' },
    { emoji: '💰', name: 'The Saver' },
    { emoji: '🤝', name: 'The Delegator' },
  ];

  return (
    <section
      id="features"
      data-testid="meet-vera-section"
      className="bg-[#FAFAF7] section-padding"
    >
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p
            data-testid="vera-section-label"
            className="font-body text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#95B8A2] mb-4"
          >
            Your AI Financial Coach
          </p>
          <h2
            data-testid="vera-section-headline"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2D2D] leading-tight"
          >
            Meet Vera. She Knows You
            <br />
            Better Than Your Bank Does.
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
          {/* Left Side - Vera Avatar */}
          <div className="w-full lg:w-2/5 flex justify-center">
            <div
              data-testid="vera-avatar"
              className="relative"
            >
              {/* Glowing background */}
              <div className="absolute inset-0 bg-[#95B8A2] rounded-full blur-3xl opacity-40 animate-pulse-glow" />
              
              {/* Avatar container */}
              <div className="relative w-64 h-64 sm:w-80 sm:h-80 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#95B8A2]/20 p-2 shadow-2xl border border-[#D4AF37]/30 animate-gold-glow">
                <div className="w-full h-full rounded-full overflow-hidden bg-[#0B1F17] flex items-center justify-center">
                  <img
                    src="https://customer-assets-4nw71qhi.emergentagent.net/job_financial-transform/artifacts/mw73m2ce_findell_coach_vera_FINAL.png"
                    alt="Vera - Your AI Financial Coach"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                
                {/* Leaf decoration */}
                <div className="absolute -bottom-2 -right-2 w-16 h-16 bg-[#1B4332] rounded-full flex items-center justify-center text-2xl shadow-lg">
                  🌿
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Feature Cards */}
          <div className="w-full lg:w-3/5 space-y-6">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                data-testid={`vera-feature-${index}`}
                className="card p-6 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1B4332]/5 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                    {feature.emoji}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-bold text-[#2D2D2D] mb-2">
                      {feature.title}
                    </h3>
                    <p className="font-body text-[#2D2D2D]/70 leading-relaxed mb-4">
                      {feature.description}
                    </p>
                    {feature.example && (
                      <div className="bg-[#1B4332] text-white rounded-2xl rounded-tl-none p-4 max-w-md">
                        <p className="font-body text-sm italic leading-relaxed">
                          {feature.example}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Money Personalities */}
            <div className="pt-4">
              <p className="font-body text-sm text-[#2D2D2D]/60 mb-3">Money Personality Types:</p>
              <div className="flex flex-wrap gap-2">
                {personalities.map((personality) => (
                  <span
                    key={personality.name}
                    data-testid={`personality-${personality.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#95B8A2]/20 rounded-full text-sm font-body text-[#1B4332]"
                  >
                    {personality.emoji} {personality.name}
                  </span>
                ))}
              </div>
              <a
                href="#academy"
                data-testid="discover-personality-button"
                className="inline-flex items-center gap-2 mt-4 font-body text-sm font-semibold text-[#1B4332] hover:text-[#D4AF37] transition-colors"
              >
                Explore Money Personalities →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MeetVeraSection;
